import { window } from "global";
import React, { useState, useEffect, useMemo, useContext } from "react";
import { renderToString } from "react-dom/server";
import classnames from "classnames";
import LPGraph from "./LPGraph";
import { UpdateDispatchContext } from "components/SummonerProfiles/SummonerProfileUpdateReducer";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import { usePlayerLP } from "api/summonerProfileApi";
import { usePatches } from "api/patchApi";
import { convertTierToValue, convertRomanNumeral } from "@ugg/shared/utils/rank-helpers";
import { ReactComponent as TriangleArrowDown } from "svg/triangle-arrow-down.svg";
import { getLPGraphTierColor, getPromotionMarkerURL } from "@ugg/shared/components/SummonerProfiles/overview/lp/lp-graph-helpers";
import { ReactComponent as TriangleArrowUp } from "svg/triangle-arrow-up.svg";

const LPBlockContainer = (props) => {
  const dispatch = useContext(UpdateDispatchContext);
  const context = getSummonerProfileContext();
  const { summonerName, region } = context;
  const { refetchedLP, queue, tier } = props;
  const [filters, setFilters] = useState({ queueType: queue });
  const { data: patches, loading: loadingPatches } = usePatches();

  const lpArgs = (patch) => [
    summonerName,
    region,
    { patchVersion: patch && patch.id },
    {
      ssr: false,
      skip: loadingPatches,
    },
  ];

  const lp1Args = lpArgs(patches && patches[0]);

  const { data: lp1, loading: loadingLP1, error: errorLP1, refetch: refetchLP1 } = usePlayerLP(...lp1Args);

  useEffect(() => {
    setFilters({ queueType: queue });

    if (!loadingPatches && patches) {
      dispatch({
        type: "addUpdates",
        payload: {
          lp1: !lp1Args[3].skip && refetchLP1,
        },
      });
    }
  }, [summonerName, region, loadingPatches]);

  const fetchingData = loadingPatches || loadingLP1;

  const data = useMemo(() => {
    if (fetchingData) {
      return null;
    }

    const MAX_DATA_POINTS = 100;
    const fullLPList = (refetchedLP || [lp1]).reduce((acc, curr, index) => {
      const { fetchPlayerLpTimeline } = curr || {};
      const timelineIndex = filters.queueType === "ranked_solo" ? 0 : 1;
      const { lpTrack } = (fetchPlayerLpTimeline && fetchPlayerLpTimeline[timelineIndex]) || {};
      const filteredLPTrack = (lpTrack || []).filter((point) => !!point.matchId && !!point.score);
      return [...acc, ...(filteredLPTrack || [])];
    }, []);
    const lpList = fullLPList.slice(-1 * MAX_DATA_POINTS);

    let min = undefined;
    let max = undefined;
    let minTier = undefined;
    let maxTier = undefined;
    const promotions = [];
    const demotions = [];
    const series = lpList.map((point, index) => {
      let promotedRank = undefined;
      let promotedTier = undefined;
      let demotedRank = undefined;
      let demotedTier = undefined;
      const rankKey = `${point.tier}_${point.rank}`.toLowerCase();
      if (index !== 0) {
        const prevPoint = lpList[index - 1];
        if ((point.tier && point.tier.toLowerCase()) === (prevPoint.tier && prevPoint.tier.toLowerCase())) {
          if (convertRomanNumeral(point.rank) < convertRomanNumeral(prevPoint.rank)) {
            // Rank promoted
            promotions.push(rankKey);
            promotedRank = rankKey;
          } else if (convertRomanNumeral(point.rank) > convertRomanNumeral(prevPoint.rank)) {
            // Rank demoted
            demotions.push(rankKey);
            demotedRank = rankKey;
          }
        } else if (convertTierToValue(point.tier) > convertTierToValue(prevPoint.tier)) {
          // Tier promoted
          promotions.push(rankKey);
          promotedTier = rankKey;
        } else if (convertTierToValue(point.tier) < convertTierToValue(prevPoint.tier)) {
          // Tier demoted
          demotions.push(rankKey);
          demotedTier = rankKey;
        }
      }
      if (isNaN(min) || min > point.score) {
        min = point.score;
        minTier = point.tier;
      }
      if (isNaN(max) || max < point.score) {
        max = point.score;
        maxTier = point.tier;
      }

      return {
        data: point,
        x: index,
        y: point.score,
        promotedRank,
        promotedTier,
        demotedRank,
        demotedTier,
        diff: lpList[0] ? point.score - lpList[0].score : 0,
        // UNCOMMENT BELOW TO ADD PROMOTION/DEMOTION MARKERS
        // dataLabels: {
        //   enabled: (promotedRank || promotedTier || demotedRank || demotedTier),
        //   useHTML: true,
        //   allowOverlap: true,
        //   formatter: function () {
        //     const { promotedRank, promotedTier, demotedRank, demotedTier, dataLabel } = this.point;
        //     const milestones = (promotedRank || promotedTier || demotedRank || demotedTier);
        //     const [tier, rank] = milestones.split("_");
        //     const classNames = classnames("lp-graph_data-label", {
        //       "lp-graph_data-label_hidden": !dataLabel,
        //       "lp-graph_data-label_promoted": (promotedRank || promotedTier),
        //       "lp-graph_data-label_promoted-inverse": (promotedRank || promotedTier) && (dataLabel && dataLabel.alignOptions.verticalAlign === "top"),
        //       "lp-graph_data-label_demoted": (demotedRank || demotedTier),
        //       "lp-graph_data-label_demoted-inverse": (demotedRank || demotedTier) && (dataLabel && dataLabel.alignOptions.verticalAlign === "bottom"),
        //       // "lp-graph_data-label_inverse": dataLabel && dataLabel.alignOptions.verticalAlign === "top"
        //     })
        //     return renderToString(
        //       <div className={classNames}>
        //         <div className="rank-text" style={{ color: "#FFFFFF" }}>
        //           {getProBuildRankName(tier, rank, true)}
        //         </div>
        //         <TriangleArrowUp className="triangle-arrow" />
        //       </div>
        //     );
        //   }
        // },
        // marker: {
        //   enabled: (promotedRank || promotedTier || demotedRank || demotedTier),
        //   states: {
        //     hover: {
        //       enabled: true
        //     }
        //   },
        //   symbol: (promotedTier || demotedTier) ? `url(${getPromotionMarkerURL((promotedTier || demotedTier).split("_")[0])})` : undefined,
        //   fillColor: getLPGraphTierColor(point.tier)
        // }
      };
    });

    return { series, min, max, minTier, maxTier };
  }, [JSON.stringify(filters), loadingPatches, lp1, refetchedLP]);

  const { series, min, max, minTier, maxTier } = data || {}; //data || {};

  let content = null;
  if (!window || fetchingData || data === undefined) {
    content = (
      <div className="lp-graph_loading">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  } else {
    content = <LPGraph series={series} min={min} max={max} tier={tier} minTier={minTier} maxTier={maxTier} />;
  }

  const totalMatchLP = series && series.length > 0 ? series[series.length - 1].diff : null;

  if (!series) {
    return (
      <div className="lp-graph_loading graph-spinner">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section content-section_no-padding lp-graph">
      <div className="header-container">
        <div className="lp-graph_header">
          <div className="flex-appart">
            <div>Last {series.length} games</div>
            {!fetchingData && totalMatchLP !== null && (
              <div className="match-lp">
                <div className="flex-center" style={{ margin: "0 2px" }}>
                  {totalMatchLP === null ? (
                    ""
                  ) : totalMatchLP === 0 ? (
                    <strong style={{ color: "#5f5f7b" }}>&ndash;</strong>
                  ) : (
                    <TriangleArrowDown className={`lp-gain-indicator ${totalMatchLP > 0 ? "gain" : "loss"}`} />
                  )}
                  <span className="match-lp-value">{Math.abs(totalMatchLP)} LP</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {content}
      <div className="flex-appart_bottom">
        <div>{series.length} games ago</div>
        <div>Last game</div>
      </div>
    </div>
  );
};

export default LPBlockContainer;
